import React from 'react';
import './SearchScopeToggle.scss';

export interface SearchScopeButton {
  /** Stable identifier persisted in the URL (derived from the label). */
  id: string;
  /** Button label shown to the user. */
  label: string;
  /** Raw Solr fq expression applied when this button is active. */
  fq: string;
  /** Optional tooltip. */
  title?: string;
}

interface SearchScopeToggleProps {
  /** Label shown before the buttons (e.g. "Search in:"). */
  label?: string;
  /** Admin-configured scope buttons. */
  buttons: SearchScopeButton[];
  /** Currently active button id ('all' when none is selected). */
  activeId: string;
  /** Called with the clicked button id. The parent toggles it off when re-clicked. */
  onSelect: (id: string) => void;
  disabled?: boolean;
}

/**
 * SearchScopeToggle - Renders a configurable set of search scope buttons.
 * Each button applies an admin-defined Solr fq expression when selected.
 * Clicking the active button clears the scope (back to "all").
 */
const SearchScopeToggle: React.FC<SearchScopeToggleProps> = ({
  label = 'Search in:',
  buttons,
  activeId,
  onSelect,
  disabled = false,
}) => {
  if (!buttons || buttons.length === 0) {
    return null;
  }

  return (
    <div className="search-scope-toggle" role="group" aria-label="Search scope">
      {label ? <span className="search-scope-label">{label}</span> : null}
      <div className="search-scope-buttons">
        {buttons.map((button) => {
          const isActive = button.id === activeId;
          return (
            <button
              key={button.id}
              type="button"
              className={`search-scope-btn ${isActive ? 'search-scope-btn--active' : ''}`}
              onClick={() => !disabled && onSelect(button.id)}
              disabled={disabled}
              title={button.title || button.label}
              aria-pressed={isActive}
            >
              <span>{button.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SearchScopeToggle;
